@if (isNewDesign) {
  <!-- UI_Version_2: outlined Previous / Next, a page size dropdown and a dark current page -->
  <div
    data-paginator-v2
    class="flex flex-wrap items-center justify-between gap-lg my-sm">
    <div class="flex items-center gap-xl">
      @if (showChangeSize()) {
        <div class="flex items-center gap-md">
          <span class="text-xs text-neutral-500">{{ sizeOptionsLabel() }}</span>
          <div
            class="relative"
            libClickOutSide
            (clickOutside)="sizeMenuOpen.set(false)">
            <button
              data-paginator-size
              type="button"
              class="flex h-8 items-center gap-sm rounded-lg border border-neutral-200 bg-white ps-md pe-sm text-sm font-medium text-neutral-900 hover:bg-neutral-50"
              aria-haspopup="listbox"
              [attr.aria-expanded]="sizeMenuOpen()"
              (click)="sizeMenuOpen.set(!sizeMenuOpen())">
              {{ currentSize() }}
              <lib-chevron-down-icon
                [iconClass]="
                  'size-4 text-neutral-500 transition-transform' +
                  (sizeMenuOpen() ? ' rotate-180' : '')
                " />
            </button>
            @if (sizeMenuOpen()) {
              <!-- Opens upwards: the paginator sits at the bottom of the page -->
              <ul
                data-paginator-size-menu
                role="listbox"
                class="absolute bottom-full start-0 z-50 mb-x-sm min-w-full rounded-lg border border-neutral-200 bg-white p-x-sm shadow-lg">
                @for (size of sizeOptions(); track size) {
                  <li
                    role="option"
                    [attr.aria-selected]="size === currentSize()">
                    <button
                      type="button"
                      class="w-full rounded-md px-md py-x-sm text-start text-sm text-neutral-900 hover:bg-neutral-100"
                      [class.font-semibold]="size === currentSize()"
                      [class.bg-neutral-100]="size === currentSize()"
                      (click)="selectSize(size)">
                      {{ size }}
                    </button>
                  </li>
                }
              </ul>
            }
          </div>
        </div>
      }
      @if (showing(); as showing) {
        <span data-paginator-summary class="text-xs text-neutral-500">
          {{
            'pagination_showing_summary'
              | transloco: { count: showing.count, total: showing.total }
          }}
        </span>
      }
    </div>

    <nav class="flex items-center" aria-label="pagination">
      <ul class="flex items-center gap-x-sm">
        <li>
          <button
            data-paginator-previous
            type="button"
            class="me-sm flex h-8 items-center gap-sm rounded-lg border border-neutral-200 bg-white px-md text-sm font-medium text-neutral-900 enabled:hover:bg-neutral-50 disabled:cursor-default disabled:border-neutral-100 disabled:text-neutral-300"
            aria-label="Go To Previous Page"
            [disabled]="currentPage() === 1"
            (click)="onPrevious()">
            @if (getPreviousIcon()) {
              <lib-chevron-down-icon [iconClass]="'size-4 rotate-90'" />
            }
            {{ previousBtnLabel() }}
          </button>
        </li>

        @for (page of pages; track $index) {
          <li>
            @if (page > 0) {
              <button
                data-paginator-page
                type="button"
                class="flex size-8 items-center justify-center rounded-lg text-sm"
                [class]="
                  page === currentPage()
                    ? 'bg-neutral-900 font-semibold text-white'
                    : 'text-neutral-700 hover:bg-neutral-100'
                "
                [attr.aria-current]="page === currentPage() ? 'page' : null"
                [attr.aria-label]="
                  page === currentPage()
                    ? 'Current Page, Page ' + page
                    : 'Go to Page ' + page
                "
                (click)="page !== currentPage() && onGoTo(page)">
                {{ page }}
              </button>
            } @else {
              <span
                class="flex size-8 items-center justify-center text-sm text-neutral-400"
                aria-hidden="true"
                >…</span
              >
            }
          </li>
        }

        <li>
          <button
            data-paginator-next
            type="button"
            class="ms-sm flex h-8 items-center gap-sm rounded-lg border border-neutral-200 bg-white px-md text-sm font-medium text-neutral-900 enabled:hover:bg-neutral-50 disabled:cursor-default disabled:border-neutral-100 disabled:text-neutral-300"
            aria-label="Go To Next Page"
            [disabled]="currentPage() === totalPages()"
            (click)="onNext()">
            {{ nextBtnLabel() }}
            @if (getNextIcon()) {
              <lib-chevron-down-icon [iconClass]="'size-4 rotate-270'" />
            }
          </button>
        </li>
      </ul>
    </nav>
  </div>
} @else {
  <div class="paginator-legacy flex items-center justify-between my-sm">
    <div class="flex items-center gap-xl">
      @if (showChangeSize()) {
        <ul class="flex justify-center gap-sm">
          <li class="flex flex-col items-center justify-center text-xs2">
            <span>{{ sizeOptionsLabel() }}</span>
          </li>

          @for (size of sizeOptions(); track $index) {
            <li class="flex flex-col items-center justify-center">
              <a
                class="justify-center items-center text-xs2 w-full rounded-sm"
                [class]="pageBtnStyle()"
                [attr.aria-current]="size === currentSize() ? 'size' : null"
                [attr.aria-label]="
                  size === currentSize()
                    ? 'Current size, size ' + size
                    : 'Go to Size ' + size
                "
                [class.current]="size === currentSize()"
                tabindex="0"
                (click)="changeSize(size)">
                {{ size }}</a
              >
            </li>
          }
        </ul>
      }
      @if (showing(); as showing) {
        <span data-paginator-summary class="text-xs2 text-neutral-500">
          {{
            'pagination_showing_summary'
              | transloco: { count: showing.count, total: showing.total }
          }}
        </span>
      }
    </div>

    <nav class="flex items-center" aria-label="pagination">
      <ul class="flex justify-center gap-sm">
        <li class="flex flex-col items-center justify-center">
          <button
            type="button"
            class="text-xs2 rounded-sm px-lg py-sm flex items-center gap-sm"
            aria-label="Go To Previous Page"
            [attr.aria-disabled]="currentPage() === 1"
            [class.opacity-30]="currentPage() === 1"
            [disabled]="currentPage() === 1"
            (click)="onPrevious()">
            @if (getPreviousIcon()) {
              <lib-chevron-down-icon
                [iconClass]="'size-4 text-neutral-500 rotate-90'" />
            }
            {{ previousBtnLabel() }}
          </button>
        </li>

        @for (page of pages; track $index) {
          <li class="flex flex-col items-center justify-center">
            <a
              class="justify-center items-center text-xs2 w-full rounded-sm"
              [class]="pageBtnStyle()"
              [attr.aria-current]="page === currentPage() ? 'page' : null"
              [attr.aria-label]="
                page === currentPage()
                  ? 'Current Page, Page ' + page
                  : 'Go to Page ' + page
              "
              [class.current]="page === currentPage()"
              [class.disable]="page < 0"
              tabindex="0"
              (click)="onGoTo(page)"
              [attr.aria-disabled]="page < 0">
              {{ page > 0 ? page : '...' }}</a
            >
          </li>
        }

        <li class="flex flex-col items-center justify-center">
          <button
            type="button"
            class="text-xs2 rounded-sm px-lg py-sm flex items-center gap-sm"
            aria-label="Go To Next Page"
            [attr.aria-disabled]="currentPage() === totalPages()"
            [class.opacity-30]="currentPage() === totalPages()"
            [disabled]="currentPage() === totalPages()"
            (click)="onNext()">
            <span> {{ nextBtnLabel() }} </span>
            @if (getNextIcon()) {
              <lib-chevron-down-icon
                [iconClass]="'size-4 text-neutral-500 rotate-270'" />
            }
          </button>
        </li>
      </ul>
    </nav>
  </div>
}
